import React, { createContext, useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { ApolloProvider } from '@apollo/client';
import { SonnerToaster } from '@artstorefronts/ui';
import { useUser, useAuth as useClerkAuth } from '@clerk/clerk-react';

import { jwtDecode } from 'jwt-decode';
import { formatDateToIso8601, getWeekStartDate, getMonthStartDate } from '@arthelper/shared/date';

import { MY_PROFILE_QUERY } from '../graphql/queries';
import {
  SIGN_UP_MUTATION,
  SIGN_IN_MUTATION,
  REFRESH_TOKEN_MUTATION,
  FORGOT_PASSWORD_MUTATION,
  RESET_PASSWORD_MUTATION,
  UPDATE_USER,
  CONVERT_FROM_PUBLIC_MUTATION,
} from '../graphql/mutations';
import { useAnalytics } from './analytics';
import { useReferrals } from './referrals';
import { getUtmParams, isGrowSurfEnabled } from '../utils';
import { getStoredAttribution, getReferralProperty } from '@arthelper/shared/attribution';
import { clearReactionCounts } from '@arthelper/shared/reaction-bar'; // pragma: allowlist secret
import { createClient, ExtendedClient } from '~/graphql/client';
import { publicChat } from '~/utils/publicChat';
import { UserProfile } from '~/types';
import { Loader2 } from 'lucide-react';
import { chatHistory } from '~/utils/chatHistory';
import { storeDeepLink } from '~/utils/deepLink';
import { messagesToChatParts } from '~/utils/messagesToChatParts';
import { PublicChatConvertingOverlay } from '~/components/PublicChatConvertingOverlay';

interface TokenData {
  access_token: string;
  refresh_token: string;
  expires_at: string;
  refresh_expires_at?: string;
}

interface ReferralInfo {
  id: string;
  email: string;
  referralCount: number;
}

interface AuthContextType {
  client: ExtendedClient;
  token: string | null;
  user: any;
  signUp: (variables: { email: string; password: string }) => Promise<void>;
  signIn: any;
  signOut: () => Promise<void>;
  getRefreshToken: (refreshToken: string) => Promise<TokenData | null>;
  getClerkToken: () => Promise<string | null>;
  forgotPassword: any;
  resetPassword: any;
  profile: any;
  loading: boolean;
  referralInfo: ReferralInfo | null;
  hasPermissions: (permissionKeys: string[]) => boolean;
  refetchProfile: () => Promise<void>;
  updateFirstUtmSource: (userId: string, utmSource: string) => Promise<void>;
}

interface User {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  createdAt: string;
  isOldAsfMember: boolean;
  isCoreFunnelLead: boolean;
  hasSettingsInstructions: boolean;
  settingsInstructions: string;
}

interface JWTTokenUserData {
  email: string;
  exp: number;
  user_id: number;
  version: number;
  website_id: number;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook for sign out logic
function useSignout({
  amplitude,
  intercom,
  setTokenData,
  setUser,
  setProfile,
  clerkSignOut,
  clientRef,
}: {
  amplitude: any;
  intercom: any;
  setTokenData: (data: TokenData | null) => void;
  setUser: (user: JWTTokenUserData | null) => void;
  setProfile: (profile: UserProfile | null) => void;
  clerkSignOut?: () => Promise<void>;
  clientRef: React.MutableRefObject<ExtendedClient | null>;
}) {
  const signOut = useCallback(async () => {
    amplitude.track('signed_out');
    await amplitude.reset();

    // Sign out of Clerk if available
    if (clerkSignOut) {
      try {
        await clerkSignOut();
      } catch (error) {
        console.error('Error signing out of Clerk:', error);
      }
    }

    // After Clerk session is gone, drop the legacy token and terminate the
    // GraphQL subscriptions socket so reconnect cannot reuse the old identity.
    clientRef.current?.updateToken(null);

    // Clear deep link to prevent it from persisting across different user sessions
    localStorage.removeItem('arthelper_deep_link');
    localStorage.removeItem('pendingMessage');
    localStorage.removeItem('pendingChatId');
    localStorage.removeItem('pendingSparkId');

    setTokenData(null);
    localStorage.removeItem('token_data');

    setUser(null);
    setProfile(null);

    // Clear all chat history when signing out
    chatHistory.clearAll();

    // Clear any other localStorage keys that might persist across sessions
    const keysToRemove = [
      'arthelper_last_conversation_id',
      'arthelper_last_content_conversation',
      'imageManagerSelectedCollection',
    ];

    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });

    // Clear all content-specific conversation keys
    const allKeys = Object.keys(localStorage);

    allKeys.forEach((key) => {
      if (key.startsWith('arthelper_last_content_conversation_')) {
        localStorage.removeItem(key);
      }
      // Clear all imageManager keys that might persist across sessions
      if (key.startsWith('imageManager')) {
        localStorage.removeItem(key);
      }
    });

    // Clear amplitude signin tracking from sessionStorage
    const sessionKeys = Object.keys(sessionStorage);
    sessionKeys.forEach((key) => {
      if (key.startsWith('amplitude_signin_tracked_')) {
        sessionStorage.removeItem(key);
      }
    });

    intercom.shutdown();
  }, [amplitude, intercom, setTokenData, setUser, setProfile, clerkSignOut, clientRef]);

  return { signOut };
}

// Hook for password reset operations
function usePasswordReset({ client }: { client: ExtendedClient | null }) {
  const forgotPassword = useCallback(
    async (variables: any) => {
      if (!client) {
        throw new Error('Client not initialized');
      }

      const res = await client.mutate({ mutation: FORGOT_PASSWORD_MUTATION, variables });

      return res.data.forgotPassword;
    },
    [client]
  );

  const resetPassword = useCallback(
    async (variables: any) => {
      if (!client) {
        throw new Error('Client not initialized');
      }

      const res = await client.mutate({
        mutation: RESET_PASSWORD_MUTATION,
        variables,
      });

      return res.data.resetPassword;
    },
    [client]
  );

  return { forgotPassword, resetPassword };
}

// Hook for authentication flows (sign up and sign in)
function useAuthFlow({
  client,
  amplitude,
  gtm,
  setTokenData,
}: {
  client: ExtendedClient | null;
  amplitude: any;
  gtm: any;
  setTokenData: (data: TokenData) => void;
}) {
  const signUp = useCallback(
    async (variables: { email: string; password: string }) => {
      if (!client) {
        throw new Error('Client not initialized');
      }

      try {
        const { data } = await client.mutate<{
          signup: {
            tokenData: Omit<TokenData, 'refresh_expires_at'>;
            user: User;
          };
        }>({
          mutation: SIGN_UP_MUTATION,
          variables,
        });

        if (data) {
          const { user, tokenData } = data.signup;

          const attribution = getStoredAttribution();

          amplitude.identify({
            id: user.id,
            email: user.email,
            username: user.username,
            firstName: user.firstName,
            lastName: user.lastName,
            createdAt: formatDateToIso8601(new Date(user.createdAt)),
            isOldAsfMember: user.isOldAsfMember,
            isCoreFunnelLead: user.isCoreFunnelLead,
            hasSettingsInstructions: user.hasSettingsInstructions,
            settingsInstructions: user.settingsInstructions,
            attribution,
          });
          amplitude.track('account_created', {
            customer_email: user.email,
            customer_id: user.id,
            ...attribution,
          });
          amplitude.track('signed_in', { ...attribution });
          gtm.push({
            event: 'signup_form_success',
            method: 'email', // Legacy signup is always email/password
          });

          setTokenData(tokenData);
          localStorage.setItem('token_data', JSON.stringify(tokenData));

          // Update the Apollo Client's token immediately
          if (client) {
            client.updateToken(tokenData.access_token);
            client.updateRefreshToken(tokenData.refresh_token);
          }
        }
      } catch (error: any) {
        if (error.message === 'US_AND_CANADA_WAITLIST') {
          window.location.href = 'https://www.arthelper.ai/waitlist';
          return;
        }
        throw error;
      }
    },
    [amplitude, client, setTokenData, gtm]
  );

  const signIn = useCallback(
    async (variables: any) => {
      if (!client) {
        throw new Error('Client not initialized');
      }

      const { data } = await client.mutate<{
        signin: {
          tokenData: TokenData;
          user: User;
        };
      }>({ mutation: SIGN_IN_MUTATION, variables });

      if (data) {
        const { tokenData } = data.signin;

        setTokenData(tokenData);
        localStorage.setItem('token_data', JSON.stringify(tokenData));

        // Update the Apollo Client's token immediately
        if (client) {
          client.updateToken(tokenData.access_token);
          client.updateRefreshToken(tokenData.refresh_token);
        }

        // Reset selected collection on login
        localStorage.removeItem('imageManagerSelectedCollection');

        amplitude.track('signed_in', { ...getStoredAttribution() });
      }
    },
    [amplitude, client, setTokenData]
  );

  return { signUp, signIn };
}

// Hook for token refresh operations
function useTokenRefresh({
  client,
  signOut,
  tokenData,
  setTokenData,
  getClerkToken,
}: {
  client: ExtendedClient | null;
  signOut: () => Promise<void>;
  tokenData: TokenData | null;
  setTokenData: (data: TokenData) => void;
  getClerkToken: () => Promise<string | null>;
}) {
  const getRefreshToken = useCallback(
    async (refreshToken: string) => {
      const refreshClient = await createClient({
        token: null,
        refreshToken: null,
        getRefreshToken: () => Promise.resolve(null),
        getClerkToken,
        signout: signOut,
      });

      try {
        const res = await refreshClient.mutate({
          mutation: REFRESH_TOKEN_MUTATION,
          variables: { refreshToken },
        });

        const refreshTokenData = res.data.refreshToken;
        const updatedTokenData = {
          ...tokenData,
          ...refreshTokenData,
        };

        // Update the current client's token without recreating it
        if (client) {
          client.updateToken(updatedTokenData.access_token);
          client.updateRefreshToken(updatedTokenData.refresh_token);
        }

        // Update localStorage and state immediately
        localStorage.setItem('token_data', JSON.stringify(updatedTokenData));
        setTokenData(updatedTokenData);

        return updatedTokenData;
      } catch (error) {
        console.error('Error refreshing token:', error);
        signOut();
        return null;
      }
    },
    [client, signOut, tokenData, setTokenData, getClerkToken]
  );

  return { getRefreshToken };
}

// Hook for Intercom boot management
function useIntercomBoot({
  user,
  profile,
  intercom,
  updateFirstUtmSource,
}: {
  user: JWTTokenUserData | null;
  profile: UserProfile | null;
  intercom: any;
  updateFirstUtmSource: (userId: string, utmSource: string) => Promise<void>;
}) {
  const [lastIntercomBoot, setLastIntercomBoot] = useState<string>('');

  useEffect(() => {
    if (user && profile) {
      const userKey = `${user.user_id}-${profile.asfId}`;

      // Only boot Intercom if user/profile combination hasn't changed
      if (lastIntercomBoot !== userKey) {
        const created_at = Math.floor(new Date(profile.createdAt).getTime() / 1000);
        const utmParams = getUtmParams(profile);

        const firstName = profile.firstName ?? '';
        const lastName = profile.lastName ?? '';
        const name = `${firstName} ${lastName}`.trim() || undefined;

        // Build profile URL and username if slug exists
        let profileUrl: string | undefined;
        let username: string | undefined;
        if (profile.profile?.slug) {
          const baseUrl = __TOWN_SQUARE_URL__;
          profileUrl = `${baseUrl}/${profile.profile.slug}`;
          username = profile.profile.slug;
        }

        const createdAtDate = new Date(profile.createdAt);
        const accountCreatedDateWeek = getWeekStartDate(createdAtDate);
        const accountCreatedDateMonth = getMonthStartDate(createdAtDate);

        intercom.boot({
          hide_default_launcher: true,
          email: user.email,
          user_id: user.user_id,
          name: name,
          created_at,
          ah_created_at: created_at,
          is_arthelper_user: true,
          is_asf_member: profile.isOldAsfMember,
          is_core_funnel_lead: profile.isCoreFunnelLead,
          has_settings_instructions: profile.hasSettingsInstructions,
          settings_instructions: profile.settingsInstructions,
          account_created_date_week: accountCreatedDateWeek,
          account_created_date_month: accountCreatedDateMonth,
          ...(username && { username }),
          ...(profileUrl && { profile_url: profileUrl }),
          ...utmParams,
        });

        setLastIntercomBoot(userKey);

        if (!profile.firstUtmSource && utmParams.first_utm_source) {
          updateFirstUtmSource(String(user.user_id), utmParams.first_utm_source);
        }
      }
    } else if (!user) {
      // Boot Intercom for anonymous users
      const anonymousKey = 'anonymous';

      if (lastIntercomBoot !== anonymousKey) {
        intercom.boot({
          hide_default_launcher: true,
        });

        setLastIntercomBoot(anonymousKey);
      }
    } else {
      intercom.shutdown();
      setLastIntercomBoot('');
    }
  }, [user, profile, intercom, updateFirstUtmSource, lastIntercomBoot]);
}

// `attachment` is a JSON scalar, so every level has to be checked before it's read.
function uploadedImageIds(parts: unknown): string[] {
  if (!Array.isArray(parts)) {
    return [];
  }

  const ids = parts.reduce((memo: Set<string>, part: unknown) => {
    const attachment = (part as { attachment?: { imageId?: unknown } } | null)?.attachment;

    if (typeof attachment?.imageId === 'string') {
      memo.add(attachment.imageId);
    }

    return memo;
  }, new Set<string>());

  // One event per image, not per part — a single image can appear on several parts.
  return [...ids];
}

// Hook for public chat conversion when user authenticates
function usePublicChatConversion({
  user,
  client,
  amplitude,
  setIsConvertingPublicChat,
}: {
  user: JWTTokenUserData | null;
  client: ExtendedClient | null;
  amplitude: any;
  setIsConvertingPublicChat: (converting: boolean) => void;
}) {
  // StrictMode remounts (and a changed `client` identity) re-run this effect while the
  // mutation is still in flight — `publicChat.clear()` only lands after it resolves.
  const convertedRef = useRef(false);

  useEffect(() => {
    if (!user || !client) return;
    if (convertedRef.current) return;

    const stored = publicChat.getForConversion();
    if (!stored || stored.messages.length === 0) return;

    convertedRef.current = true;

    const convertPublicMessages = async () => {
      try {
        setIsConvertingPublicChat(true);

        const deepLink = localStorage.getItem('arthelper_deep_link');
        const currentPath = window.location.pathname;
        const cameFromAssistant = /^\/assistant(\?|$)/.test(deepLink || '') || /^\/assistant/.test(currentPath);

        const chatParts = messagesToChatParts(stored.messages);

        const publicChatData = {
          id: stored.chatId,
          title: 'Public Chat',
          type: 'standalone',
          targetId: null,
          targetType: null,
          chatParts,
          createdAt: stored.savedAt,
          updatedAt: new Date().toISOString(),
        };

        const { data } = await client.mutate({
          mutation: CONVERT_FROM_PUBLIC_MUTATION,
          variables: { input: { publicChatData } },
        });

        const willRedirect = cameFromAssistant && Boolean(data?.convertFromPublic?.id);

        uploadedImageIds(data?.convertFromPublic?.parts).forEach((imageId) => {
          amplitude.track('image_uploaded', { image_id: imageId, source: 'guest_chat' });
        });

        publicChat.clear();

        // Clear converting state
        setIsConvertingPublicChat(false);

        // If user came from /assistant, redirect to the converted chat
        if (willRedirect) {
          localStorage.removeItem('arthelper_deep_link');
          // `track` only enqueues; this navigation would tear the page down mid-batch.
          amplitude.flushOnExit();
          window.location.href = `/chat/${data.convertFromPublic.id}`;
        }
      } catch (error) {
        console.error('Failed to convert public chat:', error);
        // Allow a later effect run to retry — `publicChat` data is still on disk.
        convertedRef.current = false;
        setIsConvertingPublicChat(false);
      }
    };

    convertPublicMessages();
  }, [user, client, amplitude, setIsConvertingPublicChat]);
}

// Hook for GrowSurf referral participant management
function useGrowSurfParticipant({
  user,
  profile,
  grsf,
  setReferralInfo,
}: {
  user: JWTTokenUserData | null;
  profile: UserProfile | null;
  grsf: any;
  setReferralInfo: (info: ReferralInfo) => void;
}) {
  useEffect(() => {
    if (!isGrowSurfEnabled() || !user || !user.email || !profile) return;

    const getParticipant = async ({ email }: { email: string }) => {
      try {
        grsf.getParticipantByEmail(email, (participant: any) => {
          if (!profile) return;

          const firstName = profile.firstName || undefined;
          const lastName = profile.lastName || undefined;

          if (participant) {
            grsf.addParticipant({ email, firstName, lastName }, (participant: any) => {
              setReferralInfo(participant);
            });
          } else {
            grsf.triggerReferral({ email, firstName, lastName }, (participant: any) => {
              setReferralInfo(participant);
            });
          }
        });
      } catch (error) {
        console.error('Error fetching participant: ', error);
      }
    };

    getParticipant({ email: user.email });
  }, [user, profile, grsf, setReferralInfo]);
}

// Hook for updating user's first UTM source
function useUpdateFirstUtmSource({ client }: { client: ExtendedClient | null }) {
  const updateFirstUtmSource = useCallback(
    async (userId: string, utmSource: string) => {
      if (!client) {
        throw new Error('Client not initialized');
      }

      await client.mutate({
        mutation: UPDATE_USER,
        variables: {
          input: {
            asfId: userId,
            firstUtmSource: utmSource,
          },
        },
      });
    },
    [client]
  );

  return { updateFirstUtmSource };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { intercom, amplitude, gtm } = useAnalytics();
  const [user, setUser] = useState<JWTTokenUserData | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [tokenData, setTokenData] = useState<TokenData | null>(() => {
    const stored = localStorage.getItem('token_data');
    return stored ? JSON.parse(stored) : null;
  });
  const [client, setClient] = useState<ExtendedClient | null>(null);
  const clientRef = useRef<ExtendedClient | null>(null);
  clientRef.current = client;
  const [loading, setLoading] = useState<boolean>(true);
  const [isConvertingPublicChat, setIsConvertingPublicChat] = useState<boolean>(false);
  const [referralInfo, setReferralInfo] = useState<ReferralInfo | null>(null);
  const token = useMemo(() => tokenData?.access_token || null, [tokenData]);
  const refreshToken = useMemo(() => tokenData?.refresh_token || null, [tokenData]);

  const { grsf } = useReferrals();

  // Clerk auth (source of truth for session state)
  const clerkAuth = useClerkAuth();
  const { getToken: getClerkToken, signOut: clerkSignOut } = clerkAuth;
  const clerkLoaded = clerkAuth.isLoaded;
  const clerkSignedIn = clerkAuth.isSignedIn;

  // Clerk user object (profile info / metadata). May lag behind auth.
  const { user: clerkUser } = useUser();

  const { signOut } = useSignout({
    amplitude,
    intercom,
    setTokenData,
    setUser,
    setProfile,
    clerkSignOut,
    clientRef,
  });
  const { forgotPassword, resetPassword } = usePasswordReset({ client });
  const { signUp, signIn } = useAuthFlow({ client, amplitude, gtm, setTokenData });
  const { updateFirstUtmSource } = useUpdateFirstUtmSource({ client });

  const getProfile = useCallback(
    async (apolloClient: ExtendedClient) => {
      try {
        const res = await apolloClient.query<{ profile: { user: UserProfile } }>({
          query: MY_PROFILE_QUERY,
          fetchPolicy: 'network-only',
        });

        const userProfile = res.data.profile?.user;

        if (!userProfile) {
          throw new Error('Profile not found');
        }

        setProfile(userProfile);

        // Call Amplitude identify whenever profile data is loaded.
        // Attribution is set as user properties so it's attached to all
        // future events for this user (Amplitude joins user props at query time).
        amplitude.identify({
          id: userProfile.asfId,
          email: userProfile.email,
          firstName: userProfile.firstName,
          lastName: userProfile.lastName,
          username: userProfile.username,
          createdAt: formatDateToIso8601(new Date(userProfile.createdAt)),
          isOldAsfMember: userProfile.isOldAsfMember,
          isCoreFunnelLead: userProfile.isCoreFunnelLead,
          hasSettingsInstructions: userProfile.hasSettingsInstructions,
          settingsInstructions: userProfile.settingsInstructions,
          // Spread separately: `getStoredAttribution()` is utm-only because marketing
          // forwards it wholesale to Intercom. The referral read is idempotent, so the
          // `signed_in` track below can carry the same property.
          attribution: { ...getStoredAttribution(), ...getReferralProperty(userProfile.asfId) },
        });
      } catch (error) {
        console.error('Error fetching profile:', error);
        if ((error as Error).message?.includes('not authenticated')) {
          signOut();
        }
      }
    },
    [signOut, amplitude]
  );

  const hasPermissions = useCallback(
    (permissionKeys: string[]) => {
      return permissionKeys.every((permissionKey) =>
        profile?.permissions?.some((permission: any) => permission.key === permissionKey)
      );
    },
    [profile]
  );

  const { getRefreshToken } = useTokenRefresh({ client, signOut, tokenData, setTokenData, getClerkToken });

  const refetchProfile = useCallback(async () => {
    if (client) {
      await getProfile(client);
    }
  }, [client, getProfile]);

  // Reaction snapshots live outside Apollo and store `hasViewerReacted`. This
  // provider stays mounted across sign-in and sign-out, and the Apollo client
  // is created once, so both the module map and the `REACTIONS_QUERY` cache
  // have to go when the viewer changes — `selectReactionCounts` falls through
  // to that cache once the map is cleared. The first null → id resolve is this
  // member's own hydration, not a switch; a later sign-in is.
  const seenReactionViewer = useRef<number | null | undefined>(undefined);
  const reactionViewerHasLeft = useRef(false);
  useEffect(() => {
    const current = user?.user_id ?? null;
    const previous = seenReactionViewer.current;
    seenReactionViewer.current = current;
    if (previous === undefined || previous === current) return;
    if (previous === null && !reactionViewerHasLeft.current) return;
    reactionViewerHasLeft.current = true;
    clearReactionCounts();
    // `update` writes this field with the previous member's `hasViewerReacted`.
    // Town-square drops it by resetting the store; this client keeps the store.
    client?.cache.evict({ id: 'ROOT_QUERY', fieldName: 'reactions' });
    client?.cache.gc();
  }, [client, user]);

  // Initialize hooks for side effects
  useIntercomBoot({ user, profile, intercom, updateFirstUtmSource });
  usePublicChatConversion({ user, client, amplitude, setIsConvertingPublicChat });
  useGrowSurfParticipant({ user, profile, grsf, setReferralInfo });

  // Initialize client only once
  useEffect(() => {
    let isMounted = true;

    async function init() {
      setLoading(true);

      try {
        const newClient = await createClient({
          token,
          refreshToken,
          getRefreshToken,
          getClerkToken,
          signout: signOut,
        });

        if (!isMounted) return;

        setClient(newClient);

        if (token) {
          const decodedUser = jwtDecode(token) as any;
          setUser(decodedUser);
          await getProfile(newClient);
        } else {
          // Don't eagerly clear here; Clerk session may exist but not hydrated yet.
          // The auth-state effect handles clearing once Clerk is loaded and signed out.
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    init();

    return () => {
      isMounted = false;
    };
    // Intentionally omit token, refreshToken, getProfile, getClerkToken from deps to avoid
    // recreating the Apollo client on every token refresh; Clerk hydration is handled
    // by the auth-state effect below.
  }, [signOut]);

  // Handle user authentication state changes (login/logout)
  useEffect(() => {
    console.log('[Auth] Auth state effect triggered', {
      hasClient: !!client,
      hasToken: !!token,
      clerkLoaded,
      clerkSignedIn,
      hasClerkUser: !!clerkUser,
      hasUser: !!user,
      pathname: window.location.pathname,
    });

    if (client) {
      // Sign-out transition detection removed - now handled by ref comparison during sign-in

      if (token) {
        // Legacy JWT authentication
        const decodedUser = jwtDecode(token) as any;
        const userChanged = !user || user.user_id !== decodedUser.user_id;
        const profileMissing = !profile;

        if (userChanged) {
          setUser(decodedUser);
        }

        // Fetch profile if user changed OR if profile is missing
        if (userChanged || profileMissing) {
          getProfile(client);
        }
      } else if (clerkSignedIn && clerkUser) {
        // Clerk authentication - map to legacy user shape
        const asfId = clerkUser.publicMetadata?.asfId as string;
        const websiteId = clerkUser.publicMetadata?.websiteId as number;

        if (!asfId) {
          // User created but webhook hasn't synced metadata yet
          // ClerkGuard will handle showing the loading state
          // Don't update ref here - wait until metadata is available
          return;
        }

        const mappedUser: JWTTokenUserData = {
          user_id: parseInt(asfId, 10),
          email: clerkUser.emailAddresses[0]?.emailAddress || '',
          exp: Math.floor(Date.now() / 1000) + 3600, // 1 hour from now
          version: 1,
          website_id: websiteId || 0,
        };

        const userChanged = !user || user.user_id !== mappedUser.user_id;
        const profileMissing = !profile;

        // Track signed_in event once per browser session when user first authenticates
        const signInTrackingKey = `amplitude_signin_tracked_${asfId}`;
        if (!sessionStorage.getItem(signInTrackingKey)) {
          console.log('[Auth] User authenticated - tracking signed_in for asfId:', asfId);
          // The referral rides this event as well as the identify: Amplitude stamps
          // user-property values onto events at ingest and never backfills earlier ones, so
          // without an event-time anchor every signup buckets under `(none)`. Spread apart
          // from `getStoredAttribution()` because marketing forwards everything that
          // function returns to Intercom as contact attributes, and the sharer's internal
          // asfId does not belong on a visitor's contact record.
          amplitude.track('signed_in', { ...getStoredAttribution(), ...getReferralProperty(asfId) });
          sessionStorage.setItem(signInTrackingKey, 'true');
        }

        // Check for new_account URL parameter (signals post-signup redirect)
        const urlParams = new URLSearchParams(window.location.search);
        const isNewAccountRedirect = urlParams.get('new_account') === 'true';

        // Also check Clerk's createdAt as backup
        const createdAt = clerkUser.createdAt ? new Date(clerkUser.createdAt) : null;
        const isRecentlyCreated = createdAt && Date.now() - createdAt.getTime() < 60000; // 60 seconds
        const isNewAccount = isNewAccountRedirect || isRecentlyCreated;

        // Determine signup method from external accounts
        let signupMethod = 'email'; // Default

        if (clerkUser.externalAccounts && clerkUser.externalAccounts.length > 0) {
          const provider = clerkUser.externalAccounts[0].provider;
          // Client-side Clerk SDK returns "google", "facebook" directly (no "oauth_" prefix)
          // Webhook payload has "oauth_google", "oauth_facebook"
          if (provider) {
            signupMethod = provider.replace('oauth_', ''); // Handle both formats
          }
        }

        // Track GTM events for new accounts only
        if (isNewAccount && !sessionStorage.getItem(`gtm_signup_tracked_${asfId}`)) {
          // event_id is the Clerk user id, which GTM's Meta CompleteRegistration tag sends as
          // eventID so Meta merges it with town-square's pixel and the API's server copy.
          gtm.push({
            event: 'signup_form_success',
            method: signupMethod,
            event_id: clerkUser.id,
          });
          sessionStorage.setItem(`gtm_signup_tracked_${asfId}`, 'true');

          // Clean up new_account param from URL
          if (isNewAccountRedirect) {
            urlParams.delete('new_account');
            const newUrl = urlParams.toString()
              ? `${window.location.pathname}?${urlParams.toString()}`
              : window.location.pathname;
            window.history.replaceState({}, '', newUrl);
          }
        }

        if (userChanged) {
          setUser(mappedUser);
        }

        // Fetch profile if user changed OR if profile is missing
        if (userChanged || profileMissing) {
          getProfile(client);
        }
      } else if (user) {
        // Clear user if logged out
        setUser(null);
        setProfile(null);

        // Clear amplitude signin tracking from sessionStorage
        const sessionKeys = Object.keys(sessionStorage);
        sessionKeys.forEach((key) => {
          if (key.startsWith('amplitude_signin_tracked_')) {
            sessionStorage.removeItem(key);
          }
        });
      }
      return;
    }

    // 2) No legacy token — do NOT decide anything until Clerk auth is hydrated
    if (!clerkLoaded) {
      return;
    }

    // Sign-out transition detection removed - now handled by ref comparison during sign-in

    // 3) Clerk authenticated path
    if (clerkSignedIn && clerkUser) {
      const asfId = clerkUser.publicMetadata?.asfId as string | undefined;
      const websiteId = clerkUser.publicMetadata?.websiteId as number | undefined;

      // If metadata hasn't arrived yet, don't clear user/profile and don't treat as signed out.
      // Just wait; ProtectedRoute should allow because clerkSignedIn is true.
      // ClerkGuard will handle showing the loading state
      // Don't update ref here - wait until metadata is available
      if (!asfId) {
        return;
      }

      const mappedUser: JWTTokenUserData = {
        user_id: parseInt(asfId, 10),
        email: clerkUser.emailAddresses[0]?.emailAddress || '',
        exp: Math.floor(Date.now() / 1000) + 3600,
        version: 1,
        website_id: websiteId || 0,
      };

      const userChanged = !user || user.user_id !== mappedUser.user_id;

      if (userChanged) {
        setUser(mappedUser);
      }
      return;
    }

    // 4) Clerk is loaded AND signed out => now safe to clear app user/profile
    if (user) {
      setUser(null);
      setProfile(null);

      // Clear amplitude signin tracking from sessionStorage
      const sessionKeys = Object.keys(sessionStorage);
      sessionKeys.forEach((key) => {
        if (key.startsWith('amplitude_signin_tracked_')) {
          sessionStorage.removeItem(key);
        }
      });
    }
  }, [client, token, clerkLoaded, clerkSignedIn, clerkUser, user, getProfile, amplitude, gtm, profile]);

  // Store deep link when token becomes null (user becomes unauthenticated)
  useEffect(() => {
    const path = window.location.pathname;
    if (path === '/signup' || path === '/signin') return;

    // Legacy token => authenticated
    if (token) return;

    // Wait for Clerk auth hydration before deciding signed-out
    if (!clerkLoaded) return;

    // Clerk signed in => authenticated
    if (clerkSignedIn) return;

    // Truly signed out
    storeDeepLink();
  }, [token, clerkLoaded, clerkSignedIn]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!client) {
    return null; // You can return an error message or fallback UI here
  }

  if (client) {
    client.setProfileRefetchCallback(() => {
      getProfile(client);
    });
  }

  return (
    <AuthContext.Provider
      value={{
        client,
        token,
        user,
        signUp,
        signIn,
        signOut,
        getRefreshToken,
        getClerkToken,
        forgotPassword,
        resetPassword,
        profile,
        loading,
        referralInfo,
        hasPermissions,
        refetchProfile,
        updateFirstUtmSource,
      }}
    >
      <ApolloProvider client={client}>
        {isConvertingPublicChat ? <PublicChatConvertingOverlay /> : children}
        <SonnerToaster />
      </ApolloProvider>
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
